import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Heart, ThumbsUp, HelpCircle, PartyPopper, Send, Trash2, Edit } from 'lucide-react';
import { communityService, Comment } from '../../services/communityService';
import { formatDistanceToNow } from 'date-fns';
import { ro } from 'date-fns/locale';
import PageHeader from '../../components/PageHeader';
import UserActionButtons, { UserActionIcons } from '../../components/community/UserActionButtons';


export default function PostDetailPage() {
  const { postId } = useParams<{ postId: string }>();
  const navigate = useNavigate();
  const storedUser = localStorage.getItem('user');
  const currentUserId = storedUser ? JSON.parse(storedUser).id : null;

  const [post, setPost] = useState<any>(null);
  const [comments, setComments] = useState<Comment[]>([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (postId) {
      loadPost();
    }
  }, [postId]);

  const loadPost = async () => {
    try {
      setLoading(true);
      const data = await communityService.getPostById(postId!);
      setPost(data.post);
      setComments(data.post.comments || []);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Eroare la încărcarea postării');
    } finally {
      setLoading(false);
    }
  };

  const handleReact = async (type: string) => {
    try {
      const userReaction = post.reactions?.[0]?.type;
      
      if (userReaction === type) {
        await communityService.removeReaction(postId!);
        setPost({
          ...post,
          reactions: [],
          likesCount: post.likesCount - 1
        });
      } else {
        await communityService.reactToPost(postId!, type as any);
        setPost({
          ...post,
          reactions: [{ type }],
          likesCount: userReaction ? post.likesCount : post.likesCount + 1
        });
      }
    } catch (err) {
      console.error('Error reacting:', err);
    }
  };

  const handleSubmitComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    try {
      setSubmitting(true);
      const comment = await communityService.createComment(postId!, {
        content: newComment,
        isAnonymous: false
      });
      setComments([...comments, comment]);
      setNewComment('');
      setPost({ ...post, commentsCount: post.commentsCount + 1 });
    } catch (err: any) {
      alert(err.response?.data?.error || 'Eroare la adăugarea comentariului');
    } finally {
      setSubmitting(false);
    }
  };

  const handleLikeComment = async (commentId: string) => {
    try {
      const comment = comments.find(c => c.id === commentId);
      if (!comment) return;

      const hasLiked = comment.likes && comment.likes.length > 0;

      if (hasLiked) {
        await communityService.unlikeComment(commentId);
        setComments(comments.map(c =>
          c.id === commentId
            ? { ...c, likes: [], likesCount: c.likesCount - 1 }
            : c
        ));
      } else {
        await communityService.likeComment(commentId);
        setComments(comments.map(c =>
          c.id === commentId
            ? { ...c, likes: [{ id: 'temp' }], likesCount: c.likesCount + 1 }
            : c
        ));
      }
    } catch (err) {
      console.error('Error liking comment:', err);
    }
  };

  const handleDeletePost = async () => {
    if (!confirm('Sigur vrei să ștergi această postare? Acțiunea nu poate fi anulată.')) return;

    try {
      setDeleting(true);
      await communityService.deletePost(post.id);
      navigate(-1);
    } catch (err: any) {
      alert(err.response?.data?.error || 'Eroare la ștergerea postării');
      setDeleting(false);
    }
  };

  const handleDeleteComment = async (commentId: string) => {
    if (!confirm('Sigur vrei să ștergi acest comentariu?')) return;

    try {
      await communityService.deleteComment(commentId);
      setComments(comments.filter(c => c.id !== commentId));
      setPost({ ...post, commentsCount: post.commentsCount - 1 });
    } catch (err: any) {
      alert(err.response?.data?.error || 'Eroare la ștergerea comentariului');
    }
  };

  const formatTimeAgo = (date: string) => {
    try {
      return formatDistanceToNow(new Date(date), { addSuffix: true, locale: ro });
    } catch {
      return 'recent';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Se încarcă...</p>
        </div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error || 'Postare negăsită'}</p>
          <button
            onClick={() => navigate(-1)}
            className="text-blue-600 hover:underline"
          >
            Înapoi
          </button>
        </div>
      </div>
    );
  }

  const userReaction = post.reactions?.[0]?.type;

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      <PageHeader title="Post" showBack={true} />

      <div className="max-w-2xl mx-auto px-3 sm:px-4 lg:px-6 py-4">
        {/* Post */}
        <div className="bg-white rounded-xl border border-gray-200 mb-4 overflow-hidden">

          {/* Header */}
          <div className="p-4">
            {/* Top row: avatar + name + time */}
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-base sm:text-lg flex-shrink-0">
                {post.isAnonymous ? '?' : (post.user.firstName?.[0] || 'U').toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-gray-900 text-sm sm:text-base truncate">
                  {post.isAnonymous ? 'Anonim' : `${post.user.firstName} ${post.user.lastName}`}
                </p>
                <p className="text-xs text-gray-500">{formatTimeAgo(post.createdAt)}</p>
              </div>
            </div>

            {/* Action buttons row — separate line on mobile */}
            <div className="flex items-center gap-2 flex-wrap">
              {/* Message + Add Friend buttons - shown for other users only (not anonymous) */}
              {post.userId !== currentUserId && !post.isAnonymous && (
                <UserActionButtons userId={post.userId} compact />
              )}

              {/* Edit & Delete buttons - shown for post owner only */}
              {post.userId === currentUserId && (
                <>
                  <button
                    onClick={() => navigate(`/community/posts/${post.id}/edit`)}
                    className="flex items-center gap-1 px-2.5 py-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors text-xs sm:text-sm"
                    title="Editează postarea"
                  >
                    <Edit className="w-4 h-4" />
                    <span className="hidden xs:inline">Editează</span>
                  </button>
                  <button
                    onClick={handleDeletePost}
                    disabled={deleting}
                    className="flex items-center gap-1 px-2.5 py-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors text-xs sm:text-sm disabled:opacity-50"
                    title="Șterge postarea"
                  >
                    <Trash2 className="w-4 h-4" />
                    <span className="hidden xs:inline">{deleting ? 'Se șterge...' : 'Șterge'}</span>
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Disease tag */}
          {post.disease && (
            <div className="px-4 pb-2">
              <span className="inline-block px-2.5 py-1 bg-gray-100 text-gray-700 text-xs rounded-full">
                {post.disease.name}
              </span>
            </div>
          )}

          {/* Title */}
          {post.title && (
            <div className="px-4 pb-2">
              <h1 className="text-lg sm:text-xl font-bold text-gray-900 leading-snug">{post.title}</h1>
            </div>
          )}

          {/* Content */}
          <div className="px-4 pb-4 text-gray-700 text-sm sm:text-base leading-relaxed whitespace-pre-wrap break-words">
            {post.content}
          </div>

          {/* Images */}
          {post.imageUrls && post.imageUrls.length > 0 && (
            <div className={`px-4 pb-4 grid gap-2 ${post.imageUrls.length === 1 ? 'grid-cols-1' : 'grid-cols-2'}`}>
              {post.imageUrls.map((url: string, index: number) => (
                <img
                  key={index}
                  src={url}
                  alt={`Image ${index + 1}`}
                  className="rounded-lg object-cover w-full max-h-64 sm:max-h-80"
                  loading="lazy"
                />
              ))}
            </div>
          )}

          {/* Reactions */}
          <div className="flex items-center border-t border-gray-100 px-2 py-1">
            <button
              onClick={() => handleReact('LIKE')}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-lg transition-all text-xs sm:text-sm font-medium ${
                userReaction === 'LIKE'
                  ? 'bg-blue-50 text-blue-600'
                  : 'text-gray-500 hover:bg-gray-50'
              }`}
            >
              <ThumbsUp className="w-4 h-4 sm:w-5 sm:h-5" />
              <span>Like</span>
            </button>
            <button
              onClick={() => handleReact('HEART')}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-lg transition-all text-xs sm:text-sm font-medium ${
                userReaction === 'HEART'
                  ? 'bg-red-50 text-red-600'
                  : 'text-gray-500 hover:bg-gray-50'
              }`}
            >
              <Heart className="w-4 h-4 sm:w-5 sm:h-5" />
              <span>Heart</span>
            </button>
            <button
              onClick={() => handleReact('SUPPORT')}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-lg transition-all text-xs sm:text-sm font-medium ${
                userReaction === 'SUPPORT'
                  ? 'bg-green-50 text-green-600'
                  : 'text-gray-500 hover:bg-gray-50'
              }`}
            >
              <HelpCircle className="w-4 h-4 sm:w-5 sm:h-5" />
              <span>Support</span>
            </button>
            <button
              onClick={() => handleReact('CELEBRATE')}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-lg transition-all text-xs sm:text-sm font-medium ${
                userReaction === 'CELEBRATE'
                  ? 'bg-yellow-50 text-yellow-600'
                  : 'text-gray-500 hover:bg-gray-50'
              }`}
            >
              <PartyPopper className="w-4 h-4 sm:w-5 sm:h-5" />
              <span className="hidden xs:inline">Celebrate</span>
              <span className="xs:hidden">🎉</span>
            </button>
          </div>
        </div>

        {/* Comments section */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-4 pt-4 pb-3 border-b border-gray-100">
            <h2 className="text-base sm:text-lg font-bold text-gray-900">
              Comentarii ({comments.length})
            </h2>
          </div>

          {/* Add comment */}
          <form onSubmit={handleSubmitComment} className="px-4 py-3 border-b border-gray-100">
            <textarea
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder="Scrie un comentariu..."
              className="w-full border border-gray-300 rounded-lg p-3 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
              rows={3}
            />
            <div className="mt-2 flex justify-end">
              <button
                type="submit"
                disabled={!newComment.trim() || submitting}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 text-sm font-medium"
              >
                <Send className="w-4 h-4" />
                {submitting ? 'Se trimite...' : 'Trimite'}
              </button>
            </div>
          </form>

          {/* Comments list */}
          <div className="divide-y divide-gray-100">
            {comments.map(comment => (
              <div key={comment.id} className="px-4 py-3">
                <div className="flex items-start gap-2.5">
                  <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-xs flex-shrink-0 mt-0.5">
                    {comment.isAnonymous ? '?' : (comment.user.firstName?.[0] || 'U').toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <div className="min-w-0">
                        <span className="font-semibold text-gray-900 text-sm">
                          {comment.isAnonymous ? 'Anonim' : `${comment.user.firstName} ${comment.user.lastName}`}
                        </span>
                        <span className="text-xs text-gray-400 ml-2">
                          {formatTimeAgo(comment.createdAt)}
                        </span>
                      </div>
                      {/* Inline action icons - only for other users' non-anonymous comments */}
                      {!comment.isAnonymous && comment.userId !== currentUserId && (
                        <div className="flex-shrink-0">
                          <UserActionIcons userId={comment.userId} />
                        </div>
                      )}
                    </div>
                    <p className="text-gray-700 text-sm leading-relaxed break-words mb-2">{comment.content}</p>

                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => handleLikeComment(comment.id)}
                        className={`flex items-center gap-1 text-xs py-1 px-2 rounded-full transition-colors ${
                          comment.likes && comment.likes.length > 0
                            ? 'text-blue-600 bg-blue-50'
                            : 'text-gray-500 hover:text-blue-600 hover:bg-blue-50'
                        }`}
                      >
                        <Heart className="w-3.5 h-3.5" />
                        <span>{comment.likesCount}</span>
                      </button>
                      {comment.userId === currentUserId && (
                        <button
                          onClick={() => handleDeleteComment(comment.id)}
                          className="text-xs text-red-500 hover:text-red-700 flex items-center gap-1 py-1 px-2 rounded-full hover:bg-red-50 transition-colors"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                          <span>Șterge</span>
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {comments.length === 0 && (
            <p className="text-center text-gray-500 text-sm py-8 px-4">
              Nu există comentarii încă. Fii primul care comentează!
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
